import { Text } from "@oysterhr/ui"
import { useTranslation } from "react-i18next"
import { ProvidedByFigures } from "./ProvidedByFigures"
import { TrustLevel } from "./TrustLevel"
import { ProvidedByOyster } from "./ProvidedByOyster"
import { convertSubunitIntoUnit, currencyCodeToSymbol } from "@oysterhr/common"
import { ProvidersEnum, SalaryBenchmark, TrustLevelEnum } from "@oysterhr/schema"

type BenchmarkLevel = "high" | "mid" | "low"

interface ProviderInsightProps {
  benchmark: SalaryBenchmark
}

export const ProviderInsight = ({ benchmark }: ProviderInsightProps) => {
  const { t } = useTranslation("knowledge_base/SalaryInsight")

  const { trustLevel, providerId, low, mid, high } = benchmark

  if (!low || !mid || !high) {
    return null
  }

  const benchmarkLevels: BenchmarkLevel[] = ["low", "mid", "high"]
  const levelToPercentile = {
    high: 75,
    mid: 50,
    low: 25,
  }
  const providedBy = {
    [ProvidersEnum.FiguresApi]: <ProvidedByFigures />,
    [ProvidersEnum.OysterPolicy]: <ProvidedByOyster />,
  }

  return (
    <div className="mb-3 rounded-md border p-3">
      <div className="mb-4">
        {providedBy[providerId]}
        {providerId === ProvidersEnum.FiguresApi && (
          <div className="mt-2">
            <TrustLevel trustLevel={trustLevel as TrustLevelEnum} />
          </div>
        )}
      </div>
      <div>
        <Text size="tiny" className="mb-1 text-gray-400">
          {t("compensationBenchmark")}
        </Text>
        <Text className="mb-1 font-medium">
          {currencyCodeToSymbol(low.currencyCode)}&nbsp;
          {Math.round(convertSubunitIntoUnit(low.subunit, low.currencyCode)).toLocaleString()}{" "}
          -&nbsp;
          {Math.round(convertSubunitIntoUnit(high.subunit, high.currencyCode)).toLocaleString()}
        </Text>

        <ul className="ml-7 list-disc text-gray-400">
          {benchmarkLevels.map((level: BenchmarkLevel) => {
            const benchmarkLevel = benchmark[level]

            if (!benchmarkLevel) {
              return null
            }

            return (
              <li key={level}>
                <Text className="text-gray-400" size="small" as="span">
                  {currencyCodeToSymbol(benchmarkLevel.currencyCode)}&nbsp;
                  {Math.round(
                    convertSubunitIntoUnit(benchmarkLevel.subunit, benchmarkLevel.currencyCode),
                  ).toLocaleString()}
                  &nbsp;
                  {providerId === ProvidersEnum.FiguresApi
                    ? t("percentile", { percentile: levelToPercentile[level] })
                    : `(${t(level)})`}
                </Text>
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}
